import 'react-data-grid/lib/styles.css';
import { type ClipboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import {
  type Column,
  DataGrid,
  type DataGridHandle,
  type RenderEditCellProps,
  type SortColumn,
} from 'react-data-grid';
import { type ClipboardTable, PasteTooLargeError, parseClipboardTable } from './clipboard';
import { ROW_STATE_LABELS } from './labels';
import { ROW_NUMBER_KEY, rowNumberColumn } from './row-number-column';
import type { RowState } from './sheet-draft';
import { Value } from './Value';

/** One column of a Master Data sheet, in its required order. */
export interface SheetColumnDef {
  readonly key: string;
  /** The business header (for the Apartments sheet, the original workbook header). */
  readonly header: string;
  /** Tells repeated headers apart, or explains the column's role. */
  readonly secondary: string | null;
  readonly group: string;
  /** Restricted columns are shown locked and never accept values (BR-047). */
  readonly available: boolean;
  readonly width: number;
  /** Identifier columns stay visible while scrolling sideways (after the row numbers). */
  readonly frozen: boolean;
}

/** How the grid reads a sheet's draft rows. */
export interface SheetGridAdapter<Row> {
  readonly key: (row: Row) => string;
  readonly display: (row: Row, key: string) => string;
  readonly state: (row: Row) => RowState;
  readonly errors: (row: Row) => Readonly<Record<string, string>>;
  readonly isCleared: (row: Row, key: string) => boolean;
  readonly isChanged: (row: Row, key: string) => boolean;
  /** Why a field cannot be changed on this row (an error code), or null. */
  readonly locked: (row: Row, key: string) => string | null;
  readonly base: (row: Row, key: string) => string;
  readonly canEdit: (row: Row, key: string) => boolean;
  readonly errorText: (key: string, code: string) => string;
  /** What a cleared cell means, for its tooltip ("יימחק" / "ההצמדה תסתיים"). */
  readonly clearedText: (key: string) => string;
}

/** A request to select and show one cell (for example from the review's "go to cell"). */
export interface CellFocusRequest {
  readonly rowKey: string;
  readonly columnKey: string;
  /** Changes with every request, so the same cell can be requested again. */
  readonly nonce: number;
}

export interface SheetGridProps<Row> {
  readonly label: string;
  readonly focus?: CellFocusRequest | null;
  readonly rows: readonly Row[];
  readonly columns: readonly SheetColumnDef[];
  readonly adapter: SheetGridAdapter<Row>;
  readonly sortColumns: readonly SortColumn[];
  readonly onSortColumnsChange: (columns: SortColumn[]) => void;
  readonly onCellChange: (row: Row, key: string, value: string) => void;
  readonly onCellClear: (row: Row, key: string) => void;
  readonly onPaste: (table: ClipboardTable, anchorIndex: number, anchorColumn: number) => void;
  readonly onMessage: (message: string, tone: 'info' | 'error') => void;
  readonly onUndo: () => void;
  readonly onRedo: () => void;
  /** Told the row of the active cell (null: none), for actions about that one row. */
  readonly onActiveRowChange?: (row: Row | null) => void;
}

interface ViewRow<Row> {
  readonly row: Row;
  readonly cells: Readonly<Record<string, string>>;
}

interface Position {
  readonly row: number;
  readonly column: number;
}
interface Range {
  readonly anchor: Position;
  readonly focus: Position;
}

const bounds = (range: Range) => ({
  top: Math.min(range.anchor.row, range.focus.row),
  bottom: Math.max(range.anchor.row, range.focus.row),
  first: Math.min(range.anchor.column, range.focus.column),
  last: Math.max(range.anchor.column, range.focus.column),
});

/** Excel's clipboard text: tab-separated, CRLF rows, quoted when a cell has a tab, break or quote. */
export function toClipboardText(table: readonly (readonly string[])[]): string {
  const cell = (value: string) =>
    /[\t\r\n"]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
  return `${table.map((row) => row.map(cell).join('\t')).join('\r\n')}\r\n`;
}

function HeaderCell({ column }: { readonly column: SheetColumnDef }) {
  return (
    <span className="sheet-header">
      <span className="sheet-header__name">{column.header}</span>
      {column.available ? (
        column.secondary !== null && (
          <span className="sheet-header__secondary">{column.secondary}</span>
        )
      ) : (
        <span className="sheet-header__secondary sheet-header__secondary--locked">
          <span aria-hidden="true">🔒</span> לא זמין עדיין
        </span>
      )}
    </span>
  );
}

function CellEditor<Row>({ row, column, onRowChange, onClose }: RenderEditCellProps<ViewRow<Row>>) {
  return (
    <input
      className="grid-editor"
      aria-label={typeof column.name === 'string' ? column.name : column.key}
      autoFocus
      value={row.cells[column.key] ?? ''}
      onChange={(event) => {
        onRowChange({ ...row, cells: { ...row.cells, [column.key]: event.target.value } });
      }}
      onBlur={() => {
        onClose(true);
      }}
    />
  );
}

/**
 * The Excel-like grid of the Master Data sheets. react-data-grid renders, virtualizes, navigates
 * and edits cells (RTL); this component adds what a sheet needs on top:
 * - an Excel-style row-number column, frozen on the right (BR-046);
 * - range selection with Shift+click / Shift+arrows (a row number selects the whole row), and
 *   Ctrl/Cmd+C copying the range as Excel text;
 * - Ctrl/Cmd+V pasting a range from the active cell (handled by the sheet's guards);
 * - Delete as an explicit, reviewable clear (DR-152), and Ctrl/Cmd+Z / Shift+Z (or Ctrl+Y) for
 *   undo and redo of unsaved edits.
 */
export function SheetGrid<Row>(props: SheetGridProps<Row>) {
  const { rows, columns, adapter } = props;
  const [range, setRange] = useState<Range | null>(null);
  const shiftHeld = useRef(false);
  const grid = useRef<DataGridHandle>(null);
  const anchor = useRef<Position>({ row: 0, column: 0 });

  useEffect(() => {
    const track = (event: KeyboardEvent) => {
      shiftHeld.current = event.shiftKey;
    };
    const release = () => {
      shiftHeld.current = false;
    };
    window.addEventListener('keydown', track);
    window.addEventListener('keyup', track);
    window.addEventListener('blur', release);
    return () => {
      window.removeEventListener('keydown', track);
      window.removeEventListener('keyup', track);
      window.removeEventListener('blur', release);
    };
  }, []);

  const viewRows = useMemo(
    () =>
      rows.map((row): ViewRow<Row> => ({
        row,
        cells: Object.fromEntries(columns.map((c) => [c.key, adapter.display(row, c.key)])),
      })),
    [rows, columns, adapter],
  );

  const rowIndex = useMemo(() => new Map(viewRows.map((view, index) => [view, index])), [viewRows]);
  const activeRow = range?.focus.row ?? -1;
  const { onActiveRowChange } = props;
  const activeItem = viewRows[activeRow]?.row ?? null;
  useEffect(() => {
    onActiveRowChange?.(activeItem);
  }, [activeItem, onActiveRowChange]);
  const area = range === null ? null : bounds(range);
  // The highlighted range: only when it spans more than one cell.
  const selection =
    area !== null && (area.top !== area.bottom || area.first !== area.last) ? area : null;
  const multi = selection !== null;

  const gridColumns = useMemo((): Column<ViewRow<Row>>[] => {
    const gutter = rowNumberColumn<ViewRow<Row>>(activeRow, (view) => {
      const state = adapter.state(view.row);
      const error = Object.keys(adapter.errors(view.row)).length > 0;
      return {
        tone: error ? 'error' : state,
        label: error ? 'שגיאה' : state === 'stored' ? null : ROW_STATE_LABELS[state],
      };
    });
    const business = columns.map((meta, index): Column<ViewRow<Row>> => {
      const key = meta.key;
      return {
        key,
        name: meta.header,
        width: meta.width,
        frozen: meta.frozen,
        sortable: meta.available,
        headerCellClass: `sheet-col sheet-col--${meta.group}${meta.available ? '' : ' sheet-col--restricted'}`,
        renderHeaderCell: () => <HeaderCell column={meta} />,
        editable: (view) => meta.available && adapter.canEdit(view.row, key),
        ...(meta.available ? { renderEditCell: CellEditor<Row> } : {}),
        cellClass: (view) => {
          const classes = [`sheet-col--${meta.group}`];
          if (!meta.available) classes.push('cell--restricted');
          else {
            const errors = adapter.errors(view.row);
            if (errors[key] !== undefined) classes.push('cell--error');
            else if (adapter.isCleared(view.row, key)) classes.push('cell--cleared');
            else if (adapter.state(view.row) === 'changed' && adapter.isChanged(view.row, key))
              classes.push('cell--changed');
            if (adapter.locked(view.row, key) !== null) classes.push('cell--locked');
          }
          if (selection !== null) {
            const at = rowIndex.get(view) ?? -1;
            if (
              at >= selection.top &&
              at <= selection.bottom &&
              index >= selection.first &&
              index <= selection.last
            )
              classes.push('cell--in-range');
          }
          return classes.join(' ');
        },
        renderCell: ({ row: view }) => {
          if (!meta.available) return <span className="visually-hidden">לא זמין עדיין</span>;
          const code = adapter.errors(view.row)[key];
          const message = code === undefined ? undefined : adapter.errorText(key, code);
          if (adapter.isCleared(view.row, key)) {
            const text = adapter.clearedText(key);
            return (
              <span title={text}>
                <del>
                  <Value text={adapter.base(view.row, key)} />
                </del>
                <span className="visually-hidden"> ({text})</span>
              </span>
            );
          }
          const locked = adapter.locked(view.row, key);
          return (
            <span title={message ?? (locked === null ? undefined : adapter.errorText(key, locked))}>
              {message !== undefined && (
                <span className="cell-error-mark" aria-hidden="true">
                  !
                </span>
              )}
              <Value text={view.cells[key] ?? ''} />
              {message !== undefined && <span className="visually-hidden"> ({message})</span>}
            </span>
          );
        },
      };
    });
    return [gutter, ...business];
  }, [activeRow, adapter, columns, rowIndex, selection]);

  const handlePaste = (event: ClipboardEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('input, textarea') !== null) return; // typing in a cell
    event.preventDefault();
    let table: ClipboardTable;
    try {
      table = parseClipboardTable(event.clipboardData.getData('text/plain'));
    } catch (error) {
      props.onMessage(
        error instanceof PasteTooLargeError
          ? 'ההדבקה גדולה מדי (עד 1,000 שורות ו-20 עמודות).'
          : 'לא ניתן לקרוא את התוכן שהודבק.',
        'error',
      );
      return;
    }
    if (table.length === 0) return;
    props.onPaste(table, anchor.current.row, anchor.current.column);
  };

  // Select and show a requested cell (for example "go to cell" from the review), once per request.
  const focusNonce = props.focus?.nonce;
  useEffect(() => {
    const request = props.focus;
    if (request == null) return;
    const rowIdx = viewRows.findIndex((view) => adapter.key(view.row) === request.rowKey);
    const idx = gridColumns.findIndex((column) => column.key === request.columnKey);
    if (rowIdx < 0 || idx < 0) return;
    // After the review dialog has closed and handed focus back, so keyboard focus lands on the cell.
    const frame = requestAnimationFrame(() => {
      grid.current?.scrollToCell({ idx, rowIdx });
      grid.current?.setActivePosition({ idx, rowIdx }, { shouldFocus: true });
    });
    return () => {
      cancelAnimationFrame(frame);
    };
    // Only a new request moves the selection, not every re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusNonce]);

  /** Ctrl/Cmd+C: the selected range (or the active cell) as Excel text; row numbers excluded. */
  const handleCopy = (event: ClipboardEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('input, textarea') !== null) return;
    if (area === null) return;
    event.preventDefault();
    const table = viewRows
      .slice(area.top, area.bottom + 1)
      .map((view) =>
        columns.slice(area.first, area.last + 1).map((column) => view.cells[column.key] ?? ''),
      );
    event.clipboardData.setData('text/plain', toClipboardText(table));
    const cells = table.length * (area.last - area.first + 1);
    props.onMessage(cells === 1 ? 'הועתק תא אחד.' : `הועתקו ${String(cells)} תאים.`, 'info');
  };

  return (
    <div className="grid-frame" onPaste={handlePaste} onCopy={handleCopy}>
      <DataGrid
        ref={grid}
        aria-label={props.label}
        aria-multiselectable={multi ? true : undefined}
        className="units-grid apartment-grid"
        direction="rtl"
        columns={gridColumns}
        rows={viewRows}
        rowKeyGetter={(view) => adapter.key(view.row)}
        rowHeight={36}
        headerRowHeight={52}
        sortColumns={props.sortColumns}
        onSortColumnsChange={props.onSortColumnsChange}
        rowClass={(view) => `row--${adapter.state(view.row)}`}
        onActivePositionChange={({ rowIdx, column }) => {
          if (rowIdx < 0) return;
          const index = columns.findIndex((c) => c.key === column?.key);
          // A row number selects the whole row, and is never a paste target itself.
          const wholeRow = column?.key === ROW_NUMBER_KEY;
          const position = {
            row: rowIdx,
            column: wholeRow ? columns.length - 1 : Math.max(0, index),
          };
          setRange((current) =>
            shiftHeld.current && current !== null
              ? { anchor: current.anchor, focus: position }
              : {
                  anchor: { row: rowIdx, column: wholeRow ? 0 : position.column },
                  focus: position,
                },
          );
          anchor.current = { row: rowIdx, column: wholeRow ? 0 : Math.max(0, index) };
        }}
        onCellKeyDown={(args, event) => {
          const mod = event.ctrlKey || event.metaKey;
          const key = event.key.toLowerCase();
          // Ctrl/Cmd+V pastes a range (handled above) instead of opening the cell editor.
          if (mod && key === 'v') {
            event.preventGridDefault();
            return;
          }
          if (args.mode !== 'ACTIVE') return;
          if (mod && (key === 'z' || key === 'y')) {
            event.preventGridDefault();
            event.preventDefault();
            if (key === 'y' || event.shiftKey) props.onRedo();
            else props.onUndo();
            return;
          }
          // Delete / Backspace: an explicit, reviewable clear of the selected cells (DR-152).
          if (event.key === 'Delete' || event.key === 'Backspace') {
            event.preventGridDefault();
            if (area === null) return;
            for (const view of viewRows.slice(area.top, area.bottom + 1)) {
              for (const column of columns.slice(area.first, area.last + 1)) {
                if (column.available && adapter.canEdit(view.row, column.key))
                  props.onCellClear(view.row, column.key);
              }
            }
          }
        }}
        onRowsChange={(next, { indexes, column }) => {
          const meta = columns.find((c) => c.key === column.key);
          if (meta?.available !== true) return;
          for (const index of indexes) {
            const view = next[index];
            if (view !== undefined && view.cells[meta.key] !== adapter.display(view.row, meta.key))
              props.onCellChange(view.row, meta.key, view.cells[meta.key] ?? '');
          }
        }}
      />
    </div>
  );
}
